import { type FC } from "react";

import { ArrowRight, Landmark, PiggyBank, Search, ShieldCheck, Wallet } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

const ASSET_SEARCH_ITEMS = [
  {
    icon: Search,
    bg: "bg-green-100",
    iconColor: "text-green-700",
    title: "10 years of financial records",
    description:
      "Accounts that don't show up anywhere else, found through credentialed IRS access.",
  },
  {
    icon: Landmark,
    bg: "bg-blue-100",
    iconColor: "text-blue-700",
    title: "Bank and investment accounts",
    description: "Checking, savings, brokerage, and stock accounts.",
  },
  {
    icon: ShieldCheck,
    bg: "bg-orange-50",
    iconColor: "text-orange-600",
    title: "Life insurance and annuities",
    description: "Unclaimed policies across US insurers.",
  },
  {
    icon: PiggyBank,
    bg: "bg-purple-100",
    iconColor: "text-purple-600",
    title: "Retirement and pensions",
    description: "401(k)s, pensions, and funds left with past employers.",
  },
  {
    icon: Wallet,
    bg: "bg-salmon-50",
    iconColor: "text-salmon-600",
    title: "Unclaimed money",
    description: "Money waiting in state and federal programs.",
  },
] as const;

interface LegalAuthorityNotSureStepProps {
  personFirstName: string;
  onBack: () => void;
  onContinue: () => void;
}

export const LegalAuthorityNotSureStep: FC<LegalAuthorityNotSureStepProps> = ({
  personFirstName,
  onBack,
  onContinue,
}) => {
  const personName = personFirstName || "your loved one";

  return (
    <LegalAuthorityStepLayout
      title="Not knowing is where most families start"
      description={`Your path continues with what you know today, and your answer can be updated anytime. Once the court grants your legal authority, Elayne Asset Search can look for you and find what was in ${personName}'s name, so you're not settling from guesswork.`}
      activeStep={3}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={onContinue}>
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      }
    >
      <Separator />

      <div className="flex flex-col gap-4 rounded-lg border border-primary bg-white p-6">
        <p className="text-sm font-medium">What an asset search finds</p>

        {ASSET_SEARCH_ITEMS.map(({ icon: Icon, bg, iconColor, title, description }) => (
          <div key={title} className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-sm ${bg}`}>
              <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-base font-medium tracking-[-0.32px]">{title}</p>
              <p className="text-sm text-secondary">{description}</p>
            </div>
          </div>
        ))}
      </div>
    </LegalAuthorityStepLayout>
  );
};
